import React, { useState, useEffect } from 'react';
import { YOUTUBE_CHANNEL_ID } from './constants';
import { Project } from './types';
import { fetchYouTubeStats, fetchLatestChannelVideos } from './services/youtubeService';
import { projectService } from './services/db';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { HomePage } from './pages/HomePage';
import { AdminPage } from './pages/AdminPage';

const App = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isYoutubeSyncing, setIsYoutubeSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  
  // Theme State
  const [isDark, setIsDark] = useState(() => {
    // Check localStorage or system preference
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('iqodex_theme');
      if (saved) return saved === 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });

  // Apply Theme
  useEffect(() => {
    const html = document.documentElement;
    if (isDark) {
      html.classList.add('dark');
      localStorage.setItem('iqodex_theme', 'dark');
    } else {
      html.classList.remove('dark');
      localStorage.setItem('iqodex_theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  // 1. Subscribe to Project Data (Firebase or Local)
  useEffect(() => {
    const unsubscribe = projectService.subscribe((data) => {
        setProjects(data);
    });
    return () => unsubscribe();
  }, []);

  // Central Sync Function
  const handleSync = async (manual = false) => {
      if (isYoutubeSyncing) return;
      
      // If no projects loaded yet, don't sync
      if (projects.length === 0) return;

      setIsYoutubeSyncing(true);

      try {
        let currentProjects = [...projects];

        let channelVideos: Partial<Project>[] = [];
        if (YOUTUBE_CHANNEL_ID && !YOUTUBE_CHANNEL_ID.includes('YOUR_CHANNEL_ID')) {
           channelVideos = await fetchLatestChannelVideos(YOUTUBE_CHANNEL_ID, manual);
        }

        const videoIdMap = new Map<string, string>();

        channelVideos.forEach(vid => {
            if (!vid.title || !vid.youtubeVideoId) return;
            const vidTitleLower = vid.title.toLowerCase();

            currentProjects.forEach(proj => {
                const projTitleLower = proj.title.toLowerCase();
                if (vidTitleLower.includes(projTitleLower)) {
                    videoIdMap.set(proj.id, vid.youtubeVideoId!);
                }
                else if (vidTitleLower.includes(`day ${proj.id}:`) || vidTitleLower.includes(`day ${proj.id} `)) {
                    videoIdMap.set(proj.id, vid.youtubeVideoId!);
                }
            });
        });

        // Update local map with found IDs
        currentProjects = currentProjects.map(p => {
            if (videoIdMap.has(p.id)) {
                return { ...p, youtubeVideoId: videoIdMap.get(p.id) };
            }
            return p;
        });

        const allVideoIds = new Set<string>();
        channelVideos.forEach(v => {
             if (v.youtubeVideoId) allVideoIds.add(v.youtubeVideoId);
        });
        currentProjects.forEach(p => {
             if (p.youtubeVideoId) allVideoIds.add(p.youtubeVideoId);
        });

        const videoDataMap = await fetchYouTubeStats(Array.from(allVideoIds));
        
        // CRITICAL FIX: Only send PARTIAL updates (id + views) to the DB.
        // Sending the full object causes race conditions where stale state (old images) 
        // overwrites new changes made by the Admin panel.
        const updates: Partial<Project>[] = [];

        currentProjects.forEach(p => {
            if (p.youtubeVideoId && videoDataMap[p.youtubeVideoId]) {
                const stats = videoDataMap[p.youtubeVideoId];
                const viewsChanged = p.views !== stats.views;
                const videoIdChanged = p.youtubeVideoId !== videoIdMap.get(p.id);
                
                if (viewsChanged || videoIdChanged) {
                    updates.push({
                        id: p.id,
                        views: stats.views ?? p.views,
                        // Only include youtubeVideoId if we found a match
                        ...(videoIdMap.has(p.id) ? { youtubeVideoId: videoIdMap.get(p.id) } : {})
                    });
                }
            }
        });

        if (updates.length > 0) {
            // Save updates back to DB/Local
            await projectService.batchUpdateProjects(updates);
        }

        setLastUpdated(Date.now());
        if (manual) {
            localStorage.setItem('yt_last_fetch', Date.now().toString());
        }

      } catch (e) {
        console.error("Sync Error", e);
      } finally {
        setIsYoutubeSyncing(false);
      }
  };

  useEffect(() => {
    // Initial sync after data load
    const timer = setTimeout(() => handleSync(false), 3000);
    return () => clearTimeout(timer);
  }, [projects.length]); // Run when projects are loaded

  useEffect(() => {
     const intervalId = setInterval(() => handleSync(false), 5 * 60 * 1000);
     return () => clearInterval(intervalId);
  }, [projects]);

  const handleAddProject = async (newProject: Project) => {
    await projectService.addProject(newProject);
    // Sync will happen via subscription update
    if (newProject.youtubeVideoId) {
        setTimeout(() => handleSync(false), 2000);
    }
  };

  const handleUpdateProject = async (updatedProject: Project) => {
    await projectService.updateProject(updatedProject);
    // Note: We intentionally DO NOT call handleSync immediately here.
    // handleSync uses the `projects` state, which is currently STALE (old data).
    // If we call it now, it might revert the change we just made.
    // We let the subscription update the `projects` state naturally, 
    // and let the periodic sync handle views later.
  };

  const handleDeleteProject = async (id: string) => {
    await projectService.deleteProject(id);
  };

  return (
    <HashRouter>
      <div className="min-h-screen relative overflow-x-hidden transition-colors duration-500">
         
         {/* Fixed Background Elements (Theme Aware) */}
         <div className="fixed inset-0 z-[-1] pointer-events-none transition-colors duration-700">
            {/* Background Base */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-cyan-50 dark:from-black dark:via-[#050505] dark:to-black transition-colors duration-700" />
            
            {/* Orbs - Darker and subtler in dark mode */}
            <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-primary/10 dark:bg-primary/10 rounded-full blur-[120px] mix-blend-multiply dark:mix-blend-screen transition-colors duration-700" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-cyan-300/20 dark:bg-cyan-900/10 rounded-full blur-[100px] mix-blend-multiply dark:mix-blend-screen transition-colors duration-700" />
         </div>

         <Routes>
           <Route path="/" element={
              <HomePage 
                projects={projects} 
                lastUpdated={lastUpdated} 
                isDark={isDark}
                toggleTheme={toggleTheme}
              />
           } />
           <Route path="/admin" element={
              <AdminPage 
                projects={projects} 
                onAddProject={handleAddProject}
                onUpdateProject={handleUpdateProject}
                onDeleteProject={handleDeleteProject}
                onSync={() => handleSync(true)}
                isSyncing={isYoutubeSyncing}
              />
           } />
         </Routes>
      </div>
    </HashRouter>
  );
};

export default App;